import { beginMutation, endMutation } from '@/composables/useScheduleFreshness';

// Centralizes user-state actions (favorites, seat reservations, My Schedule,
// profile) against the real Bizzabo/D1 endpoints.
//   favorite/unfavorite: body { sessionId: <unique_id>, email, ticketId }
//   reserve/unreserve:   ?ts=<now>&u=<ticketId>, body { session_id, ts }
// Every mutation runs inside the gate. A focus hydrate that lands mid-request
// reads the pre-change caches and writes them over the cookie, undoing the
// change the person just made — so hydrate is suppressed while any of these are
// in flight, and sibling tabs are told once the last one settles.
//
// Wrapped HERE because this is the single gateway for all six: gating at the
// call sites would mean six places to forget.
const gated = async (fn) => {
    beginMutation();
    try {
        return await fn();
    } finally {
        endMutation();
    }
};

export const useUserApi = () => {
    const { user } = useUserSession();
    // Captured synchronously here (not inside the async handlers) so the
    // useState-backed store is resolved within the composable's Nuxt context.
    const { dismiss, undismiss } = useDismissedSessions();

    const favorite = (sessionUniqueId) => gated(() =>
        $fetch('/api/v1/user/favorite', { method: 'POST', body: { sessionId: sessionUniqueId, email: user.value?.email, ticketId: user.value?.ticketId } }));

    const unfavorite = (sessionUniqueId) => gated(() =>
        $fetch('/api/v1/user/unfavorite', { method: 'POST', body: { sessionId: sessionUniqueId, email: user.value?.email, ticketId: user.value?.ticketId } }));

    const reserve = (sessionId) => gated(() => {
        const ts = Date.now();
        return $fetch(`/api/v2/reservations/register?ts=${ts}&u=${user.value?.ticketId}`, { method: 'POST', body: { session_id: sessionId, ts } });
    });

    const unreserve = (sessionId) => gated(() => {
        const ts = Date.now();
        return $fetch(`/api/v2/reservations/unregister?ts=${ts}&u=${user.value?.ticketId}`, { method: 'POST', body: { session_id: sessionId, ts } });
    });

    // Adding a session back is the user changing their mind, so it clears any
    // prior dismissal — the builder may recommend it again from now on.
    // `source` marks provenance: omit for a plain add ('local'); Find a Session
    // passes 'suggested' so its picks read as assistant-recommended (drives the
    // removal-feedback flow). 'generated' is builder-only and set server-side.
    const addToSchedule = (sessionId, { source } = {}) => gated(async () => {
        const res = await $fetch('/api/v1/schedule/add', { method: 'POST', body: { session_id: sessionId, ...(source ? { source } : {}) } });
        undismiss(sessionId);
        return res;
    });

    // Removing a session marks it dismissed (Asana 687632): Find a Session and
    // the Schedule Builder won't recommend it back until the user re-adds it.
    const removeFromSchedule = (sessionId) => gated(async () => {
        const res = await $fetch('/api/v1/schedule/remove', { method: 'POST', body: { session_id: sessionId } });
        dismiss(sessionId);
        return res;
    });

    // Non-mutating: does this session clash with anything already scheduled?
    // Only the full-session flow needs it, because that flow decides what to show
    // BEFORE attempting the add — see conflict-check.get.js.
    const checkConflict = (sessionUniqueId) =>
        $fetch('/api/v1/schedule/conflict-check', { query: { session_id: sessionUniqueId } });

    const me = () => $fetch('/api/v1/user/me');

    return { favorite, unfavorite, reserve, unreserve, addToSchedule, removeFromSchedule, checkConflict, me };
};
